import { WEIGHT_RANGE_KG } from '../dashboard/calculate-muscle';

export { WEIGHT_RANGE_KG };

export type WeightReading = { ok: true; kg: number | undefined } | { ok: false; error: string };

/**
 * The weight entered at the start of a scan, or why it cannot be used.
 *
 * Only what was typed for this scan counts. The field used to be pre-filled
 * from the profile — set when the client was created and never updated — and
 * an untouched field recorded that figure as the day's weight. It now offers
 * the last weight as a one-tap hint instead, so a recorded weight is always
 * one somebody stated for this scan.
 *
 * The range is the dashboard's: a weight it would refuse to use for body
 * composition is refused here, at the one moment someone can correct it.
 */
export const readWeight = (input: string, required: boolean): WeightReading => {
    const text = input.trim().replace(',', '.');
    if (!text) {
        return required
            ? { ok: false, error: "Enter today's weight. It is needed to split fat from lean." }
            : { ok: true, kg: undefined };
    }
    const kg = Number(text);
    if (!Number.isFinite(kg) || kg < WEIGHT_RANGE_KG.min || kg > WEIGHT_RANGE_KG.max) {
        return {
            ok: false,
            error: `Weight must be between ${WEIGHT_RANGE_KG.min} and ${WEIGHT_RANGE_KG.max} kg.`,
        };
    }
    return { ok: true, kg };
};

/** "Between 30 and 300 kg." for a form field, or null when the text is fine. */
export const weightFieldProblem = (input: string): string | null => {
    const text = input.trim().replace(',', '.');
    if (!text) return null;
    const kg = Number(text);
    return Number.isFinite(kg) && kg >= WEIGHT_RANGE_KG.min && kg <= WEIGHT_RANGE_KG.max
        ? null
        : `Between ${WEIGHT_RANGE_KG.min} and ${WEIGHT_RANGE_KG.max} kg.`;
};

/** BMI outside this is asked about, not refused: rare, but people exist there. */
export const PLAUSIBLE_BMI = { min: 14, max: 60 };

/** A change from the last weight larger than this is asked about. */
export const LARGE_CHANGE = 0.2;

/**
 * A question to ask before using a weight that is in range but looks like a
 * typo — "18" for "81", "65" for "56" — or null. The range cannot catch
 * these; the client's height and last weight can.
 */
export const weightDoubt = (
    kg: number,
    { heightCm, lastKg }: { heightCm?: number; lastKg?: number | null },
): string | null => {
    if (heightCm !== undefined && heightCm >= 100 && heightCm <= 250) {
        const bmi = kg / (heightCm / 100) ** 2;
        if (bmi < PLAUSIBLE_BMI.min || bmi > PLAUSIBLE_BMI.max) {
            return `That is a BMI of ${Math.round(bmi)} at ${Math.round(heightCm)} cm. Is ${kg} kg right?`;
        }
    }
    if (
        lastKg != null &&
        lastKg >= WEIGHT_RANGE_KG.min &&
        lastKg <= WEIGHT_RANGE_KG.max &&
        Math.abs(kg - lastKg) / lastKg > LARGE_CHANGE
    ) {
        return `Last time was ${lastKg} kg. Is ${kg} kg right?`;
    }
    return null;
};
